import { Box, Grid, Typography } from "@mui/material";

import { InfoDisclaimerBox } from "@covertree/ui/content";
import { Button, CustomAccordion } from "@covertree/ui";
import { sortAdditionalInterests } from "@covertree/features";
import { RemoveAdditionalInterestConfirmationModal } from "@components/AdditionalInterestModals";

import { useAdditionalInterest } from "./useAdditionalInterest";
import { AdditionalInterestForm } from "./AdditionalInterestForm/AdditionalInterestForm";
import { AdditionalInterestItem } from "./AdditionalInterestItem/AdditionalInterestItem";
import { theme } from "@covertree/theme";

interface AdditionalInterestProps {
  viewOnly?: boolean;
}

/** Additional Interest section — add, edit, replace, or remove interests. */
export const AdditionalInterest = ({ viewOnly }: AdditionalInterestProps) => {
  const {
    expandOnInit,
    sectionRef,
    additionalInterests,
    isTenantPolicy,
    isAdding,
    editingId,
    addFormRef,
    editFormRef,
    interestsCountByType,
    interestToRemove,
    handleAddClick,
    handleFormClose,
    handleAddSubmit,
    handleEditClick,
    handleEditClose,
    handleEditSubmit,
    handleRemoveClick,
    handleRemoveClose,
    handleRemoveConfirm,
  } = useAdditionalInterest();

  // Add button is hidden behind the feature flag from useAdditionalInterests.
  const addButton = (
    <Button
      variant="outlined"
      size="small"
      tooltipPlacement="top"
      disabled={viewOnly}
      onClick={handleAddClick}>
      Add additional interest
    </Button>
  );

  const addForm = isAdding ? (
    <Box ref={addFormRef}>
      <AdditionalInterestForm
        onSubmit={handleAddSubmit}
        onClose={handleFormClose}
        interestsCountByType={interestsCountByType}
        isTenantPolicy={isTenantPolicy}
      />
    </Box>
  ) : null;

  return (
    <Box
      ref={sectionRef}
      position="relative"
      border="1px solid"
      borderRadius="20px"
      paddingX={3}
      paddingY={1}
      marginY={1}
      width={1}
      borderColor="action.disabled"
      sx={{
        "& .MuiAccordion-root.Mui-expanded": { margin: 0 },
        "& .MuiAccordionSummary-root.Mui-expanded": { minHeight: 48 },
        "& .MuiAccordionSummary-expandIconWrapper svg": {
          color: `${theme.palette.text.secondary} !important`,
        },
      }}>
      <CustomAccordion
        dataTestId="additional-interest-accordion"
        expandOnInit={expandOnInit}
        arrowToRight
        withoutBorder
        contentSx={{ paddingX: 0 }}
        header={() => (
          <Typography variant="h5" fontSize={18}>
            Additional Interest
          </Typography>
        )}
        content={
          <Box pb={1}>
            <Box mb={2}>
              <InfoDisclaimerBox
                fontSize={14}
                data-testid="billing-party-info-box">
                Only the billing party is responsible for paying the premium.
                You can change the billing party only if they were added at
                policy issuance. Otherwise, you can add, edit, or remove
                notification recipients here.
              </InfoDisclaimerBox>
            </Box>
            {additionalInterests?.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                mt={isAdding ? 2 : 8}>
                {!isAdding && (
                  <Typography variant="subtitle1" color="text.disabled">
                    Nothing here yet
                  </Typography>
                )}
                {!isAdding && addButton}
                {addForm}
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="flex-end" mb={2}>
                  {addButton}
                </Box>

                {addForm}

                <Grid container mt={2} spacing={2}>
                  {sortAdditionalInterests(additionalInterests).map(
                    (additionalInterest) => (
                      <AdditionalInterestItem
                        key={additionalInterest.additionalInterestId}
                        additionalInterest={additionalInterest}
                        viewOnly={viewOnly}
                        editingId={editingId}
                        editFormRef={editFormRef}
                        interestsCountByType={interestsCountByType}
                        isTenantPolicy={isTenantPolicy}
                        onEditClick={handleEditClick}
                        onEditClose={handleEditClose}
                        onEditSubmit={handleEditSubmit}
                        onRemoveClick={handleRemoveClick}
                      />
                    )
                  )}
                </Grid>
              </>
            )}

            <RemoveAdditionalInterestConfirmationModal
              isModalOpen={interestToRemove !== null}
              additionalInterestName={interestToRemove?.name ?? ""}
              onClose={handleRemoveClose}
              onConfirm={handleRemoveConfirm}
            />
          </Box>
        }
      />
    </Box>
  );
};
